import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { FaMapMarkerAlt, FaSlash, FaPalette, FaCalendarAlt } from 'react-icons/fa';
import { CiEdit } from "react-icons/ci";
import { MdDelete } from "react-icons/md";
import 'bootstrap/dist/css/bootstrap.min.css';
import ReactDatePicker from 'react-datepicker';
import ColorPicker from '../../common/ColorPicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../styles/components/itinerary/ColumnTitle.css';
import Portal from '../../common/Portal';

const ColumnTitle = ({
  title,
  index,
  isEditing,
  startEditing,
  handleTitleChange,
  handleBlur,
  handleKeyDown,
  handleDelete,
  handleDateChange,
  date,
  toggleMarkers,
  toggleColorPicker,
  isVisible,
  colorPickerVisible,
  handleChangeComplete,
  color,
  colorPickerRef,
  readOnly
}) => {
  const [selectedDate, setSelectedDate] = useState(date || null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);

  const onDateChange = (date) => {
    setSelectedDate(date);
    handleDateChange(index, date);
    setIsDatePickerOpen(false);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    const daySuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };
    return `${day}${daySuffix(day)} ${month} ${year}`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    if (isDropdownOpen && toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [isDropdownOpen]);

  return (
    <div className="column-title-container" ref={dropdownRef} draggable={false} onDragStart={(e) => e.preventDefault()}>
      {isEditing ? (
        <input
          id={`column-input-${index}`}
          type="text"
          className="column-edit-input"
          value={title}
          onChange={handleTitleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      ) : (
        <div>
          <div className="title-icon-container">
            <h3 className="column-title" id={`column-title-${index}`} onClick={startEditing}>
              {title}
            </h3>
            {!readOnly && (
              <CiEdit
                className="edit-icon-column"
                onClick={startEditing}
                role="button"
              />
            )}
          </div>
          {selectedDate && <p className="column-date">{formatDate(selectedDate)}</p>}
        </div>
      )}

      <Dropdown
        className="icon-dropdown"
        onToggle={(isOpen) => {
          setIsDropdownOpen(isOpen);
        }}
      >
        <Dropdown.Toggle
          variant="secondary"
          id={`dropdown-basic-${index}`}
          className="dropdown-toggle-custom"
          ref={toggleRef}
        >
          <BsThreeDotsVertical className="dropdown-icon" />
        </Dropdown.Toggle>

        <Portal>
          {isDropdownOpen && dropdownPosition && (
            <Dropdown.Menu className="column-title-dropdown-menu" style={{ position: 'absolute', top: dropdownPosition.top, left: dropdownPosition.left, transition: 'none' }} onClick={(e) => e.stopPropagation()}>
              {!readOnly && (
                <>
                  <Dropdown.Item onClick={handleDelete} className="column-title-dropdown-item">
                    <MdDelete alt="Delete" className="column-title-dropdown-item-icon" />
                    Delete Column
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setIsDatePickerOpen(true)} className="column-title-dropdown-item">
                    <FaCalendarAlt className="column-title-dropdown-item-icon" />
                    Set Date
                  </Dropdown.Item>
                </>
              )}
              <Dropdown.Item onClick={toggleMarkers} className="column-title-dropdown-item">
                <div className="column-title-dropdown-item-icon">
                  <FaMapMarkerAlt className="icon-marker" />
                  {!isVisible && <FaSlash className="icon-slash" />}
                </div>
                {isVisible ? 'Hide Markers' : 'Show Markers'}
              </Dropdown.Item>
              <Dropdown.Item onClick={toggleColorPicker} className="column-title-dropdown-item">
                <FaPalette className="column-title-dropdown-item-icon" />
                Change Marker Color
              </Dropdown.Item>
            </Dropdown.Menu>
          )}
        </Portal>
      </Dropdown>

      {isDatePickerOpen && dropdownPosition && (
        <Portal>
          <div className="date-picker-overlay" style={{ position: 'absolute', top: dropdownPosition.top, left: dropdownPosition.left }}>
            <ReactDatePicker
              selected={selectedDate}
              onChange={onDateChange}
              onClickOutside={() => setIsDatePickerOpen(false)}
              inline
            />
          </div>
        </Portal>
      )}

      {colorPickerVisible && dropdownPosition && (
        <Portal>
          <div className="color-picker-popover" ref={colorPickerRef} style={{ position: 'absolute', top: dropdownPosition.top, left: dropdownPosition.left }}>
            <ColorPicker color={color} onChangeComplete={handleChangeComplete} />
          </div>
        </Portal>
      )}
    </div>
  );
};

ColumnTitle.propTypes = {
  title: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isEditing: PropTypes.bool.isRequired,
  startEditing: PropTypes.func.isRequired,
  handleTitleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleKeyDown: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleDateChange: PropTypes.func.isRequired,
  date: PropTypes.instanceOf(Date),
  toggleMarkers: PropTypes.func.isRequired,
  toggleColorPicker: PropTypes.func.isRequired,
  isVisible: PropTypes.bool.isRequired,
  colorPickerVisible: PropTypes.bool.isRequired,
  handleChangeComplete: PropTypes.func.isRequired,
  color: PropTypes.string.isRequired,
  colorPickerRef: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
};

export default ColumnTitle;
